export interface Account {
	id: string;
	username: string;
	avatar: string;
	discordId: string;
	email: string;
	global_name: string;
	role: string;
	suspend: boolean;
	credit: number;
	wishlist: string[];
}

export type AccountLoadFailureKind =
	| "unauthenticated"
	| "forbidden"
	| "missing-account"
	| "transient";

export class AccountLoadError extends Error {
	readonly kind: AccountLoadFailureKind;

	constructor(kind: AccountLoadFailureKind, message = "Account profile unavailable") {
		super(message);
		this.name = "AccountLoadError";
		this.kind = kind;
	}
}

export interface AccountSessionBackend {
	startDiscordSignIn: () => Promise<string>;
	exchangeDiscordCode: (callback: DiscordCallback) => Promise<void>;
	loadAccount: () => Promise<Account>;
	logout: () => Promise<void>;
}

export type DiscordCallback = {
	code: string;
	state: string;
};

export interface AccountSessionPersistence {
	writeReturnPath: (path: string) => void;
	takeReturnPath: () => string | null;
	clearReturnPath: () => void;
}

export interface AccountSessionSnapshot {
	account: Account | null;
	loading: boolean;
	profileStatus: "idle" | "ready" | "transient-error" | "signed-out";
}

export type DiscordSignInResult =
	| {
		status: "signed-in";
		account: Account;
		returnTo: string;
	}
	| {
		status: "ignored";
	};

export interface AccountSession {
	getSnapshot: () => AccountSessionSnapshot;
	subscribe: (listener: () => void) => () => void;
	initialize: () => Promise<boolean>;
	refresh: () => Promise<boolean>;
	logout: () => Promise<void>;
	beginDiscordSignIn: (returnTo?: string) => Promise<string>;
	completeDiscordSignIn: (
		callback: DiscordCallback,
	) => Promise<DiscordSignInResult>;
	cancelDiscordSignIn: () => void;
}

type CreateAccountSessionOptions = {
	backend: AccountSessionBackend;
	persistence: AccountSessionPersistence;
};

const RETURN_PATH_BASE = "https://account-session.invalid";

function safeReturnPath(path: string | null | undefined): string {
	if (!path?.startsWith("/") || path.startsWith("//")) return "/";
	try {
		const resolved = new URL(path, RETURN_PATH_BASE);
		return resolved.origin === RETURN_PATH_BASE ? path : "/";
	} catch {
		return "/";
	}
}

export function createAccountSession({
	backend,
	persistence,
}: CreateAccountSessionOptions): AccountSession {
	let snapshot: AccountSessionSnapshot = {
		account: null,
		loading: true,
		profileStatus: "idle",
	};
	let revision = 0;
	let initializePromise: Promise<boolean> | null = null;
	let refreshPromise: Promise<boolean> | null = null;
	const handledDiscordStates = new Set<string>();
	const listeners = new Set<() => void>();

	const publish = (nextSnapshot: AccountSessionSnapshot) => {
		if (
			snapshot.account === nextSnapshot.account
			&& snapshot.loading === nextSnapshot.loading
			&& snapshot.profileStatus === nextSnapshot.profileStatus
		) {
			return;
		}
		snapshot = nextSnapshot;
		listeners.forEach((listener) => listener());
	};

	const invalidateProfileLoads = () => {
		revision += 1;
		refreshPromise = null;
	};

	const logout = async () => {
		invalidateProfileLoads();
		await backend.logout();
		invalidateProfileLoads();
		persistence.clearReturnPath();
		publish({
			account: null,
			loading: false,
			profileStatus: "signed-out",
		});
	};

	const refresh = (): Promise<boolean> => {
		if (refreshPromise) return refreshPromise;

		const operationRevision = revision + 1;
		revision = operationRevision;
		const request = (async () => {
			try {
				const account = await backend.loadAccount();
				if (revision !== operationRevision) {
					return false;
				}
				publish({
					account,
					loading: false,
					profileStatus: "ready",
				});
				return true;
			} catch (error) {
				if (revision === operationRevision) {
					const kind = error instanceof AccountLoadError
						? error.kind
						: "transient";
					publish({
						account: kind === "transient" ? snapshot.account : null,
						loading: false,
						profileStatus: kind === "transient"
							? "transient-error"
							: "signed-out",
					});
				}
				return false;
			}
		})();
		const trackedPromise = request.finally(() => {
			if (revision === operationRevision) {
				refreshPromise = null;
			}
		});
		refreshPromise = trackedPromise;
		return trackedPromise;
	};

	const initialize = () => {
		if (!initializePromise) {
			const request = refresh();
			const trackedPromise = request.finally(() => {
				if (initializePromise === trackedPromise) {
					initializePromise = null;
				}
			});
			initializePromise = trackedPromise;
		}
		return initializePromise;
	};

	const beginDiscordSignIn = async (returnTo?: string) => {
		if (returnTo) {
			persistence.writeReturnPath(safeReturnPath(returnTo));
		} else {
			persistence.clearReturnPath();
		}
		try {
			return await backend.startDiscordSignIn();
		} catch (error) {
			persistence.clearReturnPath();
			throw error;
		}
	};

	const completeDiscordSignIn = async ({
		code,
		state,
	}: DiscordCallback): Promise<DiscordSignInResult> => {
		if (!code || !state || handledDiscordStates.has(state)) {
			return { status: "ignored" };
		}
		handledDiscordStates.add(state);

		let sessionEstablished = false;
		try {
			await backend.exchangeDiscordCode({ code, state });
			sessionEstablished = true;
			invalidateProfileLoads();
			if (!(await refresh()) || !snapshot.account) {
				throw new Error("Account profile could not be loaded");
			}
			const returnTo = safeReturnPath(persistence.takeReturnPath());
			return {
				status: "signed-in",
				account: snapshot.account,
				returnTo,
			};
		} catch (error) {
			if (sessionEstablished) {
				await backend.logout().catch(() => undefined);
				invalidateProfileLoads();
				publish({
					account: null,
					loading: false,
					profileStatus: "signed-out",
				});
			}
			persistence.clearReturnPath();
			throw error;
		}
	};

	return {
		getSnapshot: () => snapshot,
		subscribe: (listener) => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},
		initialize,
		refresh,
		logout,
		beginDiscordSignIn,
		completeDiscordSignIn,
		cancelDiscordSignIn: () => persistence.clearReturnPath(),
	};
}
